import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import { Icon } from "@/libraries/wpl/foundations/src";

const sectionStyles = css({
    "background": "linear-gradient(0deg, #ffffff 0%, #e6fcff 100%)",
    "padding": `${token("space.1000")} ${token("space.200")} ${token(
        "space.800"
    )}`,
    ".text-section": {
        h4: {
            "margin": `0 0 ${token("space.100")}`,
            "fontWeight": 500,
            "@media (max-width: 47.99rem)": {
                fontSize: "1.25rem",
                lineHeight: "1.625rem"
            },
            "@media (min-width: 48rem) and (max-width: 63.99rem)": {
                fontSize: "1.375rem",
                lineHeight: "1.75rem"
            },
            "@media (min-width: 64rem)": {
                fontSize: "1.5rem",
                lineHeight: "1.875rem"
            }
        },
        p: {
            margin: 0,
            fontWeight: 400,
            color: "#091E42",
            fontSize: "1rem",
            lineHeight: "1.5rem"
        }
    },
    ".contentBlock-body": {
        color: "#253858"
    }
});

const sectionH2 = css({
    "fontWeight": 500,
    "textAlign": "center",
    "color": "#091E42",
    "margin": 0,
    "@media (max-width: 47.99rem)": {
        fontSize: "2rem",
        lineHeight: "2.375rem"
    },
    "@media (min-width: 48rem) and (max-width: 63.99rem)": {
        fontSize: "2.25rem",
        lineHeight: "2.625rem"
    },
    "@media (min-width: 64rem)": {
        fontSize: "2.5rem",
        lineHeight: "3rem"
    }
});

const outerGridStyles = css({
    maxWidth: "1200px",
    width: "100%",
    margin: `${token("space.200")} auto 0`,
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    [media.below.md]: {
        gridTemplateColumns: "1fr"
    }
});

const innerGridStyles = css({
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    [media.below.sm]: {
        gridTemplateColumns: "1fr"
    }
});

const columnStyles = css({
    display: "block",
    padding: token("space.200")
});

const iconStyles = css({
    display: "flex",
    borderRadius: "50%",
    backgroundColor: "#DEEBFF",
    width: "60px",
    height: "60px",
    marginBlockEnd: token("space.200")
});

const innerIconStyles = css({
    margin: "auto",
    width: "min-content",
    height: "min-content"
});

const WhyConfluenceSection = () => {
    const intl = useIntl();

    const sectionContent = {
        communicationTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.communicationTitle",
            defaultMessage: "Visual communication"
        }),
        communicationBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.communicationBody",
            defaultMessage:
                "Bring your ideas to life: from brainstorming in whiteboards to project plans in pages."
        }),
        organizationScalesTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.organizationScalesTitle",
            defaultMessage: "Organization that scales"
        }),
        organizationScalesBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.organizationScalesBody",
            defaultMessage:
                "No matter your team size, projects and company knowledge are always findable and up-to-date."
        }),
        infoProtectionTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.infoProtectionTitle",
            defaultMessage: "Information protection"
        }),
        infoProtectionBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.infoProtectionBody",
            defaultMessage:
                "Permission settings let you control who can view or edit pages, keeping information private."
        }),
        aiPoweredTitle: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.aiPoweredTitle",
            defaultMessage: "AI-powered productivity"
        }),
        aiPoweredBody: intl.formatMessage({
            id: "ConfluenceSoftwarePageTemplate.whyConfluenceSection.aiPoweredBody",
            defaultMessage:
                "Turn messy notes into useful docs, summarize in seconds, and search for relevant answers."
        })
    };

    return (
        <section css={sectionStyles}>
            <h2 css={sectionH2}>
                <FormattedMessage
                    id="ConfluenceSoftwarePageTemplate.whyConfluenceSection.header"
                    defaultMessage="Why Confluence?"
                    values={DEFAULT_VALUES}
                />
            </h2>

            <div css={outerGridStyles}>
                <div css={innerGridStyles}>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="lightbulb"
                                    size="small"
                                    color="#172b4d"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.communicationTitle}
                            bodyText={sectionContent.communicationBody}
                        />
                    </div>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="building"
                                    size="small"
                                    color="#172b4d"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.organizationScalesTitle}
                            bodyText={sectionContent.organizationScalesBody}
                        />
                    </div>
                </div>
                <div css={innerGridStyles}>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="lock"
                                    size="small"
                                    color="#172b4d"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.infoProtectionTitle}
                            bodyText={sectionContent.infoProtectionBody}
                        />
                    </div>
                    <div css={columnStyles}>
                        <div css={iconStyles}>
                            <div css={innerIconStyles}>
                                <Icon
                                    name="circleunions"
                                    size="small"
                                    color="#172b4d"
                                />
                            </div>
                        </div>
                        <ContentBlock
                            headerType="h4"
                            headerText={sectionContent.aiPoweredTitle}
                            bodyText={sectionContent.aiPoweredBody}
                        />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default WhyConfluenceSection;
